/**
 * /dashboard/announcements — full list of tenant announcements (G11).
 *
 * The dashboard hub's <AnnouncementsRibbon /> shows the top 3 pinned
 * ones with a "View all (N)" link that lands here. This page lists
 * EVERY active announcement (pinned-first, then by posted_at desc).
 * Read-only — HR-admin CRUD happens via the backend (UI for it lives
 * in Settings later).
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Megaphone, Pin } from "lucide-react";
import { PageHeader } from "@/components/shared/page-header";
import { listAnnouncements } from "@/lib/api/announcements";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toApiError } from "@/lib/api";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/_authgated/app/$tenantSlug/announcements")({
  component: AnnouncementsPage,
});

function fmtPostedAt(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function AnnouncementsPage() {
  const q = useQuery({
    queryKey: ["announcements", "list"],
    queryFn: ({ signal }) => listAnnouncements(signal),
    staleTime: 60_000,
  });

  // Pinned first, then most-recent-first within each bucket.
  const sorted = useMemo(() => {
    const items = q.data?.items ?? [];
    return [...items].sort((a, b) => {
      if (a.is_pinned !== b.is_pinned) return a.is_pinned ? -1 : 1;
      return b.posted_at.localeCompare(a.posted_at);
    });
  }, [q.data]);

  return (
    <div className="space-y-4">
      <PageHeader
        title="Announcements"
        description="Notes from your HR-admins, pinned items first."
      />

      {q.isLoading && (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <Skeleton key={i} className="h-24 w-full" />
          ))}
        </div>
      )}

      {q.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn&apos;t load announcements</AlertTitle>
          <AlertDescription>{toApiError(q.error).message}</AlertDescription>
        </Alert>
      )}

      {q.isSuccess && sorted.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <div className="flex h-14 w-14 items-center justify-center rounded-full bg-muted text-muted-foreground">
              <Megaphone className="h-7 w-7" />
            </div>
            <p className="text-base font-medium text-foreground">
              No announcements yet
            </p>
            <p className="max-w-md text-sm text-muted-foreground">
              When an HR-admin posts a company-wide note it&apos;ll show
              up here.
            </p>
          </CardContent>
        </Card>
      )}

      {q.isSuccess && sorted.length > 0 && (
        <div className="space-y-3">
          {sorted.map((a) => (
            <Card key={a.id}>
              <CardContent className="space-y-2 pt-5">
                <div className="flex flex-wrap items-center gap-2">
                  {a.is_pinned && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
                      <Pin className="h-3 w-3" />
                      Pinned
                    </span>
                  )}
                  <h2 className="text-base font-semibold text-foreground">
                    {a.title}
                  </h2>
                  <span className="ml-auto text-xs text-muted-foreground">
                    {fmtPostedAt(a.posted_at)}
                  </span>
                </div>
                <p className="whitespace-pre-wrap text-sm text-muted-foreground">
                  {a.body}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
